import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { Subscription } from 'rxjs';

export interface FilterSelectOption<T = string | number> {
  value: T;
  label: string;
  facetCount?: number;
}

@Component({
  selector: 'app-filter-multi-select',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatListModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './filter-multi-select.component.html',
  styleUrl: './filter-multi-select.component.scss',
})
export class FilterMultiSelectComponent<
  T extends string | number = string | number,
> implements OnDestroy {
  @Input() placeholder = 'Vælg…';
  @Input() searchPlaceholder = 'Søg…';
  @Input() displayText = '';
  @Input() options: FilterSelectOption<T>[] = [];
  @Input() selectedValues: T[] = [];
  @Input() inOverlay = false;
  @Input() mobileOverlay = false;
  @Output() selectedValuesChange = new EventEmitter<T[]>();
  @Output() searchChange = new EventEmitter<string>();

  @ViewChild('triggerBtn') triggerBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownTemplate') dropdownTemplate?: TemplateRef<unknown>;

  searchQuery = '';
  isOpen = false;

  private overlayRef: OverlayRef | null = null;
  private backdropSub: Subscription | null = null;
  private outsidePointerHandler: ((event: PointerEvent) => void) | null = null;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnDestroy(): void {
    this.close();
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
      return;
    }
    this.open();
  }

  open(): void {
    const trigger = this.triggerBtn?.nativeElement;
    const template = this.dropdownTemplate;
    if (!trigger || !template || this.overlayRef) {
      return;
    }

    const panel =
      (trigger.closest('.filter-panel') as HTMLElement | null) ?? trigger;
    const panelRect = panel.getBoundingClientRect();
    const useMobileSheet = this.inOverlay && this.mobileOverlay;

    const positionStrategy = useMobileSheet
      ? this.overlay.position().global().bottom('0').centerHorizontally()
      : this.overlay
          .position()
          .flexibleConnectedTo(panel)
          .withPositions([
            {
              originX: 'start',
              originY: 'bottom',
              overlayX: 'start',
              overlayY: 'top',
              offsetY: 4,
            },
            {
              originX: 'start',
              originY: 'top',
              overlayX: 'start',
              overlayY: 'bottom',
              offsetY: -4,
            },
          ])
          .withPush(true)
          .withViewportMargin(8);

    const useInOverlayLayer = this.inOverlay;

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.inOverlay
        ? this.overlay.scrollStrategies.block()
        : this.overlay.scrollStrategies.reposition(),
      hasBackdrop: !useInOverlayLayer,
      backdropClass: 'filter-multi-select-backdrop',
      panelClass: useMobileSheet
        ? ['filter-multi-select-overlay-pane', 'mobile-sheet']
        : 'filter-multi-select-overlay-pane',
      width: useMobileSheet ? '100vw' : panelRect.width,
      maxHeight: useMobileSheet
        ? 'min(320px, 50vh)'
        : this.inOverlay
          ? 'min(280px, 45vh)'
          : 'min(360px, 60vh)',
      disposeOnNavigation: true,
    });

    const portal = new TemplatePortal(template, this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.isOpen = true;

    const dropdownPane = this.overlayRef.overlayElement;
    dropdownPane.style.setProperty('z-index', '1003', 'important');
    dropdownPane.style.setProperty('pointer-events', 'auto', 'important');

    if (!useInOverlayLayer) {
      this.backdropSub = this.overlayRef
        .backdropClick()
        .subscribe(() => this.close());
    } else {
      this.outsidePointerHandler = (event: PointerEvent) => {
        const path = event.composedPath();
        const insideDropdown =
          !!this.overlayRef?.overlayElement &&
          path.includes(this.overlayRef.overlayElement);
        const onTrigger =
          !!this.triggerBtn?.nativeElement &&
          path.includes(this.triggerBtn.nativeElement);
        if (insideDropdown || onTrigger) {
          return;
        }
        this.close();
      };
      document.addEventListener('pointerdown', this.outsidePointerHandler, true);
    }

    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  close(): void {
    this.backdropSub?.unsubscribe();
    this.backdropSub = null;

    if (this.outsidePointerHandler) {
      document.removeEventListener('pointerdown', this.outsidePointerHandler, true);
      this.outsidePointerHandler = null;
    }

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }

    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.clearSearch();
  }

  onSelectionChange(values: T[]): void {
    this.selectedValuesChange.emit(values);
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.searchChange.emit(value);
  }

  clearSearch(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.searchQuery = '';
    this.searchChange.emit('');
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }
}
